import httpClient from './httpClient';
import type { PaginatedResult } from '@/types';
import type {
  CustomerListItem,
  CustomerDetail,
  CustomerSearchItem,
  CustomerFilters,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  SalesOrderListItem,
  SalesOrderDetail,
  SalesOrderFilters,
  SalesOrderLineDto,
  SalesOrderLineInput,
  CreateSalesOrderRequest,
  UpdateSalesOrderRequest,
  AddSalesOrderLineRequest,
} from '@/types/sales';

export const salesApi = {
  // ── Customers ─────────────────────────────────────────────

  async getCustomers(mdFilters: CustomerFilters): Promise<PaginatedResult<CustomerListItem>> {
    const mdParams: Record<string, string | number | boolean> = {
      page: mdFilters.page,
      pageSize: mdFilters.pageSize,
    };
    if (mdFilters.search) mdParams.search = mdFilters.search;
    if (mdFilters.customerType) mdParams.customerType = mdFilters.customerType;
    if (mdFilters.isActive !== undefined) mdParams.isActive = mdFilters.isActive;

    const mdResponse = await httpClient.get<PaginatedResult<CustomerListItem>>(
      '/customers',
      { params: mdParams }
    );
    return mdResponse.data;
  },

  async getCustomerById(mdId: string): Promise<CustomerDetail> {
    const mdResponse = await httpClient.get<CustomerDetail>(`/customers/${mdId}`);
    return mdResponse.data;
  },

  async searchCustomers(mdQuery: string): Promise<CustomerSearchItem[]> {
    const mdResponse = await httpClient.get<CustomerSearchItem[]>(
      '/customers/search',
      { params: { q: mdQuery } }
    );
    return mdResponse.data;
  },

  /** Повертає Guid створеного клієнта */
  async createCustomer(mdRequest: CreateCustomerRequest): Promise<string> {
    const mdResponse = await httpClient.post<string>('/customers', mdRequest);
    return mdResponse.data;
  },

  async updateCustomer(mdRequest: UpdateCustomerRequest): Promise<void> {
    await httpClient.put(`/customers/${mdRequest.id}`, mdRequest);
  },

  // ── Sales Orders ───────────────────────────────────────────

  async getSalesOrders(
    mdFilters: SalesOrderFilters
  ): Promise<PaginatedResult<SalesOrderListItem>> {
    const mdParams: Record<string, string | number> = {
      page: mdFilters.page,
      pageSize: mdFilters.pageSize,
    };
    if (mdFilters.search) mdParams.search = mdFilters.search;
    if (mdFilters.customerId) mdParams.customerId = mdFilters.customerId;
    if (mdFilters.status) mdParams.status = mdFilters.status;
    if (mdFilters.dateFrom) mdParams.dateFrom = mdFilters.dateFrom;
    if (mdFilters.dateTo) mdParams.dateTo = mdFilters.dateTo;

    const mdResponse = await httpClient.get<PaginatedResult<SalesOrderListItem>>(
      '/sales-orders',
      { params: mdParams }
    );
    return mdResponse.data;
  },

  async getSalesOrderById(mdId: string): Promise<SalesOrderDetail> {
    const mdResponse = await httpClient.get<SalesOrderDetail>(`/sales-orders/${mdId}`);
    return mdResponse.data;
  },

  /** Повертає Guid створеного замовлення */
  async createSalesOrder(mdRequest: CreateSalesOrderRequest): Promise<string> {
    const mdResponse = await httpClient.post<string>('/sales-orders', mdRequest);
    return mdResponse.data;
  },

  async updateSalesOrder(mdRequest: UpdateSalesOrderRequest): Promise<void> {
    await httpClient.put(`/sales-orders/${mdRequest.id}`, mdRequest);
  },

  async confirmSalesOrder(mdId: string): Promise<void> {
    await httpClient.post(`/sales-orders/${mdId}/confirm`);
  },

  async cancelSalesOrder(mdId: string, mdReason: string): Promise<void> {
    // Сервер звіряє id з тіла з id маршруту
    await httpClient.post(`/sales-orders/${mdId}/cancel`, { id: mdId, reason: mdReason });
  },

  /** Повертає Guid доданого рядка */
  async addSalesOrderLine(
    mdOrderId: string,
    mdLine: Omit<AddSalesOrderLineRequest, 'salesOrderId'>
  ): Promise<string> {
    const mdRequest: AddSalesOrderLineRequest = { ...mdLine, salesOrderId: mdOrderId };
    const mdResponse = await httpClient.post<string>(
      `/sales-orders/${mdOrderId}/lines`,
      mdRequest
    );
    return mdResponse.data;
  },

  async removeSalesOrderLine(mdOrderId: string, mdLineId: string): Promise<void> {
    await httpClient.delete(`/sales-orders/${mdOrderId}/lines/${mdLineId}`);
  },

  /**
   * PUT не приймає рядків — приводимо рядки чернетки до стану форми через
   * DELETE/POST lines. Змінений рядок перестворюється (окремого PUT для рядка немає).
   */
  async syncSalesOrderLines(
    mdOrderId: string,
    mdOriginalLines: SalesOrderLineDto[],
    mdLines: SalesOrderLineInput[]
  ): Promise<void> {
    const mdKept = new Set<string>();
    for (const mdLine of mdLines) {
      const mdOriginal = mdLine.id
        ? mdOriginalLines.find((mdO) => mdO.id === mdLine.id)
        : undefined;
      if (
        mdOriginal &&
        mdOriginal.productId === mdLine.productId &&
        mdOriginal.quantityOrdered === mdLine.quantity &&
        mdOriginal.unitPrice === mdLine.unitPrice &&
        mdOriginal.discountPercent === mdLine.discountPercent
      ) {
        mdKept.add(mdOriginal.id);
      }
    }

    for (const mdOriginal of mdOriginalLines) {
      if (!mdKept.has(mdOriginal.id)) {
        await salesApi.removeSalesOrderLine(mdOrderId, mdOriginal.id);
      }
    }

    for (const mdLine of mdLines) {
      if (mdLine.id && mdKept.has(mdLine.id)) continue;
      await salesApi.addSalesOrderLine(mdOrderId, {
        productId: mdLine.productId,
        quantity: mdLine.quantity,
        unitPrice: mdLine.unitPrice,
        discountPercent: mdLine.discountPercent,
      });
    }
  },
};
